import { QRCodeDialog } from "@/components/QRCodeDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CurrencyIcon } from "@/components/ui/CurrencyIcon";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { AddFundsWizard } from "@/components/wallet/AddFundsWizard";
import { WithdrawWizard } from "@/components/wallet/WithdrawWizard";
import { useWalletBalances, useWalletTransactions } from "@/features/user/wallet/hooks/useWallet";
import { TransactionCard } from "@/features/user/wallet/TransactionCard";
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Page, PageHeader, PageContent } from "@supabank/ui";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowLeft, Copy, ExternalLink, Plus, QrCode, Wallet } from "lucide-react";
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { toast } from '@/hooks/use-toast';

/**
 * WalletDetailPage - Revolut-style: детальная страница конкретного wallet
 * 
 * Показывает:
 * - Баланс этого wallet
 * - Адрес wallet (с копированием)
 * - Транзакции этого wallet
 * - Действия: Add Funds, Withdraw, View on Explorer
 * 
 * Design Principles (matching VirtualAccountDetailPage):
 * - Skeleton loading
 * - Структура как в VirtualAccountDetailPage
 * - Консистентный spacing
 * - Краткие описания
 */
export default function WalletDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Wizard state
  const [isAddFundsOpen, setAddFundsOpen] = useState(false);
  const [isWithdrawOpen, setWithdrawOpen] = useState(false);

  // Get all wallets and find the one we need
  const { data: wallets, isLoading: walletsLoading, error: walletsError } = useWalletBalances();
  const wallet = useMemo(() => {
    if (!wallets || !id) return null;
    return wallets.find(w => w.id === id);
  }, [wallets, id]);

  // Get transactions for this wallet (filter by wallet_id if API supports it)
  // For now, we'll filter on client side
  const { data: allTransactions, isLoading: transactionsLoading } = useWalletTransactions();
  const walletTransactions = useMemo(() => {
    if (!allTransactions || !wallet) return [];
    // Filter transactions that involve this wallet
    // This is a simplified filter - in production, API should support wallet_id filter
    return allTransactions.filter(tx => {
      // Check if transaction involves this wallet's address or bridge_wallet_id
      const sourceWalletId = (tx.source as any)?.bridge_wallet_id;
      const destWalletId = (tx.destination as any)?.bridge_wallet_id;
      return sourceWalletId === wallet.bridge_wallet_id || destWalletId === wallet.bridge_wallet_id;
    });
  }, [allTransactions, wallet]);

  const isLoading = walletsLoading || transactionsLoading;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getExplorerUrl = (chain: string, address: string) => {
    const explorers: Record<string, string> = {
      ethereum: `https://etherscan.io/address/${address}`,
      base: `https://basescan.org/address/${address}`,
      solana: `https://solscan.io/account/${address}`,
    };
    return explorers[chain.toLowerCase()] || `https://explorer.xyz/${chain}/${address}`;
  };

  // Calculate total balance for this wallet
  const totalUsdValue = useMemo(() => {
    if (!wallet) return 0;
    return wallet.balances.reduce((sum, b) => sum + parseFloat(b.usd_value || '0'), 0);
  }, [wallet]);

  if (isLoading) {
    return (
      <Page data-ux-contract="canon" className={semanticTokens.spacing.lg}>
        <PageHeader
          icon={Wallet}
          title="Wallet Details"
          subtitle="Loading..."
        />
        <PageContent>
          <div className={cn("space-y-6", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
            <Skeleton className={cn("h-64 w-full skeleton", semanticTokens.radius.lg)} />
            <Skeleton className={cn("h-48 w-full skeleton", semanticTokens.radius.lg)} />
          </div>
        </PageContent>
      </Page>
    );
  }

  if (walletsError || !wallet) {
    return (
      <Page data-ux-contract="canon" className={semanticTokens.spacing.lg}>
        <PageHeader
          icon={Wallet}
          title="Wallet Details"
          subtitle="Wallet not found"
        />
        <PageContent>
          <EmptyState
            icon={Wallet}
            title="Wallet not found"
            description="The requested wallet could not be found or you don't have access to it."
            action={{
              label: "Back to Wallet",
              onClick: () => navigate('/dashboard/wallet')
            }}
          />
        </PageContent>
      </Page>
    );
  }

  // Get primary balance (first non-zero or first available)
  const primaryBalance = wallet.balances.find(b => parseFloat(b.balance) > 0) || wallet.balances[0];
  const currency = primaryBalance?.currency || 'USD';
  const chain = wallet.chain;

  return (
    <Page data-ux-contract="canon" className={semanticTokens.spacing.lg}>
      {/* Back Button - Desktop only (mobile uses Page.Header back button) */}
      <div className="mb-4 hidden lg:block">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard/wallet')}
          className={cn(
            "focus-visible:ring-2 focus-visible:ring-blue-500",
            semanticTokens.motion.standard,
            "min-h-[44px]"
          )}
          aria-label="Back to wallet"
        >
          <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
          Back to Wallet
        </Button>
      </div>

      {/* Header */}
      <PageHeader
        icon={Wallet}
        title={`${currency.toUpperCase()} Wallet`}
        subtitle={`${chain} • ${wallet.address.slice(0, 8)}...${wallet.address.slice(-6)}`}
        showBackButton={true}
        onBack={() => navigate('/dashboard/wallet')}
      />

      <PageContent>
        <div className={cn("space-y-8 lg:space-y-12", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
          {/* Balance Section */}
          <Card>
            <CardHeader>
              <CardTitle className={cn(semanticTokens.typography.h3)}>
                Balance
              </CardTitle>
            </CardHeader>
            <CardContent className={cn("space-y-6", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
              {/* Primary Balance Display */}
              <div className="flex items-center gap-6">
                <div className={cn(
                  "flex-shrink-0",
                  "w-16 h-16 lg:w-20 lg:h-20",
                  semanticTokens.bg.muted,
                  semanticTokens.radius.full,
                  "flex items-center justify-center p-1"
                )}>
                  <CurrencyIcon
                    symbol={currency}
                    size={56}
                    className={cn(
                      "shrink-0",
                      "w-[56px] h-[56px] lg:w-[72px] lg:h-[72px]",
                      "[&>img]:!w-full [&>img]:!h-full [&>img]:!min-w-0 [&>img]:!min-h-0",
                      semanticTokens.fg.default
                    )}
                  />
                </div>
                <div className="flex-1">
                  <div className={cn(
                    "font-display text-display-md lg:text-display-lg",
                    semanticTokens.fg.default
                  )}>
                    {primaryBalance ? `${parseFloat(primaryBalance.balance).toLocaleString('en-US', { maximumFractionDigits: 6 })} ${currency.toUpperCase()}` : '0.00'}
                  </div>
                  {totalUsdValue > 0 && (
                    <div className={cn(
                      semanticTokens.typography.body,
                      semanticTokens.fg.muted,
                      "mt-2"
                    )}>
                      ≈ ${totalUsdValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  )}
                </div>
              </div>

              {/* All Balances */}
              {wallet.balances.length > 1 && (
                <div className={cn("pt-4 border-t", semanticTokens.border.default)}>
                  <div className={cn(semanticTokens.typography.label, "mb-3")}>
                    All Balances
                  </div>
                  <div className="space-y-2">
                    {wallet.balances.map((balance) => (
                      <div key={balance.currency} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CurrencyIcon
                            symbol={balance.currency}
                            size="sm"
                            className={cn("shrink-0", semanticTokens.fg.default)}
                          />
                          <span className={cn(semanticTokens.typography.body, "font-medium")}>{balance.currency.toUpperCase()}</span>
                        </div>
                        <div className="text-right">
                          <div className={semanticTokens.typography.body}>
                            {parseFloat(balance.balance).toLocaleString('en-US', { maximumFractionDigits: 6 })}
                          </div>
                          {balance.usd_value && parseFloat(balance.usd_value) > 0 && (
                            <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                              ≈ ${parseFloat(balance.usd_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className={cn("pt-4 border-t", semanticTokens.border.default)}>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => setAddFundsOpen(true)}
                    className={cn(
                      "gap-2 min-h-[44px]",
                      semanticTokens.motion.standard,
                      semanticTokens.focus.ring
                    )}
                    aria-label="Add funds to wallet"
                  >
                    <Plus className="w-4 h-4" aria-hidden="true" />
                    Add Funds
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setWithdrawOpen(true)}
                    className={cn(
                      "gap-2 min-h-[44px]",
                      semanticTokens.motion.standard,
                      semanticTokens.focus.ring
                    )}
                    aria-label="Withdraw from wallet"
                  >
                    <ArrowDownLeft className="w-4 h-4" aria-hidden="true" />
                    Withdraw
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(getExplorerUrl(chain, wallet.address), '_blank')}
                    className={cn(
                      "gap-2 min-h-[44px]",
                      semanticTokens.motion.standard,
                      semanticTokens.focus.ring
                    )}
                    aria-label="View wallet on blockchain explorer"
                  >
                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                    View on Explorer
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Your Address Section - Clean, Simple, User-Focused */}
          {wallet.deposit_address ? (
            // User has a deposit address - show only this one (no need for internal wallet address)
            <Card>
              <CardHeader>
                <CardTitle className={cn(semanticTokens.typography.h3)}>
                  Your Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={cn(
                  "flex items-center justify-between p-4 rounded-lg",
                  semanticTokens.bg.muted
                )}>
                  <div className="flex-1 min-w-0">
                    <code className={cn(
                      "font-mono text-sm break-all",
                      semanticTokens.fg.default
                    )}>
                      {wallet.deposit_address}
                    </code>
                    <div className={cn(
                      semanticTokens.typography.caption,
                      semanticTokens.fg.muted,
                      "mt-1"
                    )}>
                      {chain} network
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <QRCodeDialog
                      address={wallet.deposit_address}
                      asset={currency}
                      chain={chain}
                      label={`${currency} Address`}
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "min-h-[44px] min-w-[44px]",
                            semanticTokens.motion.standard,
                            semanticTokens.focus.ring
                          )}
                          aria-label="Show QR code"
                        >
                          <QrCode className="w-4 h-4" aria-hidden="true" />
                        </Button>
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(wallet.deposit_address!)}
                      className={cn(
                        "min-h-[44px] min-w-[44px]",
                        semanticTokens.motion.standard,
                        semanticTokens.focus.ring
                      )}
                      aria-label="Copy address"
                    >
                      <Copy className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>

                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                  Send {currency} to this address to add funds to your wallet.
                </p>
              </CardContent>
            </Card>
          ) : (
            // Fallback: No deposit address yet - show wallet address with setup prompt
            <Card>
              <CardHeader>
                <CardTitle className={cn(semanticTokens.typography.h3)}>
                  Wallet Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={cn(
                  "flex items-center justify-between p-4 rounded-lg",
                  semanticTokens.bg.muted
                )}>
                  <div className="flex-1 min-w-0">
                    <code className={cn(
                      "font-mono text-sm break-all",
                      semanticTokens.fg.default
                    )}>
                      {wallet.address}
                    </code>
                    <div className={cn(
                      semanticTokens.typography.caption,
                      semanticTokens.fg.muted,
                      "mt-1"
                    )}>
                      {chain} network
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <QRCodeDialog
                      address={wallet.address}
                      asset={currency}
                      chain={chain}
                      label={`${currency} Address`}
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "min-h-[44px] min-w-[44px]",
                            semanticTokens.motion.standard,
                            semanticTokens.focus.ring
                          )}
                          aria-label="Show QR code"
                        >
                          <QrCode className="w-4 h-4" aria-hidden="true" />
                        </Button>
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(wallet.address)}
                      className={cn(
                        "min-h-[44px] min-w-[44px]",
                        semanticTokens.motion.standard,
                        semanticTokens.focus.ring
                      )}
                      aria-label="Copy address"
                    >
                      <Copy className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>

                <div className={cn(
                  "p-3 rounded-lg",
                  semanticTokens.bg.muted
                )}>
                  <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                    Deposits may take longer to appear. Contact support to set up faster deposits.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Separator */}
          <div className={cn("border-t", semanticTokens.border.default)} />

          {/* Transactions Section */}
          <div>
            <h2 className={cn(semanticTokens.typography.h2, "mb-4")}>
              Transactions
            </h2>
            {transactionsLoading ? (
              <div className={cn("space-y-4", semanticTokens.spacing.md.replace('p-', 'space-y-'))}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className={cn("h-24 skeleton", semanticTokens.radius.lg)} />
                ))}
              </div>
            ) : walletTransactions.length === 0 ? (
              <EmptyState
                variant="no-data"
                illustration={
                  <div className={cn(
                    'w-20 h-20 rounded-full flex items-center justify-center',
                    semanticTokens.radius.full,
                    semanticTokens.bg.muted
                  )}>
                    <Wallet className={cn('h-10 w-10', semanticTokens.fg.muted)} aria-hidden="true" />
                  </div>
                }
                title="No transactions yet"
                description="Transactions for this wallet will appear here once you start using it."
              />
            ) : (
              <div className={cn("space-y-4", semanticTokens.spacing.md.replace('p-', 'space-y-'))}>
                {walletTransactions.map((tx) => (
                  <TransactionCard
                    key={tx.id}
                    transaction={tx}
                    onClick={() => navigate(`/dashboard/transactions/${tx.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </PageContent>

      {/* Add Funds Wizard - Sheet */}
      <AddFundsWizard
        open={isAddFundsOpen}
        onClose={() => setAddFundsOpen(false)}
        onSuccess={() => {
          // Invalidate and refetch wallet data
          queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
          queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
        }}
      />

      {/* Withdraw Wizard - Sheet */}
      <WithdrawWizard
        open={isWithdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        onSuccess={() => {
          // Invalidate and refetch wallet data
          queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
          queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
        }}
      />

    </Page>
  );
}

